import { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { joinEntity, saveSession } from '@/domains/auth/services/auth.service';
import { COUNTRY_DIAL_CODES } from '@/utils/countryDialCodes';
import { INTERNAL_ROLES } from '@/shared/types/users.types';

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8000/api';
const INPUT = 'w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent';

interface InvitationInfo {
    entity_name: string;
    entity_type: string;
    role: string;           // rôle proposé : 'client' | 'community-delivery' | 'admin'…
    invited_by: string;
    expires_at: string;
}

export default function JoinPage() {
    const { token = '' } = useParams<{ token: string }>();
    const navigate = useNavigate();

    const [invitation, setInvitation] = useState<InvitationInfo | null>(null);
    const [invLoading, setInvLoading] = useState(true);
    const [invError, setInvError] = useState('');

    // Formulaire
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phone, setPhone] = useState('');
    const [countryCode, setCountry] = useState('+224');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPwd, setConfirmPwd] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // ── Charger les infos de l'invitation ─────────────────────
    useEffect(() => {
        (async () => {
            try {
                const res = await fetch(`${API_BASE}/join/${token}`);
                if (!res.ok) throw new Error('Invitation invalide ou expirée.');
                const data: InvitationInfo = await res.json();
                setInvitation(data);
            } catch (e: unknown) {
                setInvError(e instanceof Error ? e.message : 'Invitation invalide.');
            } finally {
                setInvLoading(false);
            }
        })();
    }, [token]);

    // ── Soumission ─────────────────────────────────────────────
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!firstName || !lastName || !phone || !password) {
            setError('Veuillez remplir tous les champs obligatoires.');
            return;
        }
        if (password.length < 8) {
            setError('Mot de passe : 8 caractères minimum.');
            return;
        }
        if (password !== confirmPwd) {
            setError('Les mots de passe ne correspondent pas.');
            return;
        }

        setError('');
        setLoading(true);

        try {
            const slug = `${lastName}-${firstName}-${Date.now()}`
                .toLowerCase().replace(/\s+/g, '-');

            const data = await joinEntity(token, {
                first_name: firstName.trim(),
                last_name: lastName.trim(),
                phone: `${countryCode}${phone.replace(/\D/g, '')}`,
                email: email.trim() || undefined,
                password,
                slug,
            });

            if (data.token) {
                const session = saveSession({ ...data, token: data.token });
                navigate(session.dashboardPath);
            } else {
                navigate('/login?registered=1');
            }
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : 'Une erreur est survenue.');
        } finally {
            setLoading(false);
        }
    };

    // ── Rendu état chargement / erreur invitation ──────────────
    if (invLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <i className="ri-loader-4-line text-3xl text-teal-600 animate-spin mb-3 block" />
                    <p className="text-gray-500 text-sm">Vérification de l'invitation…</p>
                </div>
            </div>
        );
    }

    if (invError || !invitation) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center p-8 max-w-md">
                    <i className="ri-error-warning-line text-4xl text-red-500 mb-4 block" />
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">Invitation invalide</h1>
                    <p className="text-gray-500 mb-6">{invError}</p>
                    <Link to="/" className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700">
                        Retour à l'accueil
                    </Link>
                </div>
            </div>
        );
    }

    // Infos du rôle interne proposé
    const roleInfo = INTERNAL_ROLES.find(r => r.id === invitation.role);

    return (
        <div className="min-h-screen bg-gradient-to-br from-teal-50 to-gray-100 flex flex-col">

            {/* Header */}
            <header className="bg-white shadow-sm border-b border-gray-100">
                <div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-between">
                    <Link to="/" className="text-2xl font-bold text-teal-600"
                        style={{ fontFamily: 'Pacifico, serif' }}>
                        ONAPILA
                    </Link>
                </div>
            </header>

            <div className="flex-1 flex items-center justify-center px-4 py-10">
                <div className="w-full max-w-md">

                    {/* Bandeau invitation */}
                    <div className="bg-teal-600 text-white rounded-2xl p-6 mb-6 text-center">
                        <i className="ri-user-add-line text-3xl mb-2 block" />
                        <p className="text-sm opacity-80 mb-1">
                            <strong>{invitation.invited_by}</strong> vous invite à rejoindre
                        </p>
                        <h1 className="text-2xl font-bold mb-1">{invitation.entity_name}</h1>
                        {roleInfo && (
                            <div className="inline-flex items-center gap-2 bg-white/20 rounded-lg px-3 py-1 text-sm mt-1">
                                <i className={roleInfo.icon} />
                                {roleInfo.label}
                            </div>
                        )}
                    </div>

                    {/* Formulaire */}
                    <div className="bg-white rounded-2xl shadow-xl p-8">
                        <h2 className="text-xl font-semibold text-gray-900 mb-5">
                            Créez votre compte membre
                        </h2>

                        {error && (
                            <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm flex gap-2">
                                <i className="ri-error-warning-line mt-0.5 shrink-0" />
                                <span>{error}</span>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                        Prénom <span className="text-red-500">*</span>
                                    </label>
                                    <input type="text" value={firstName}
                                        onChange={e => setFirstName(e.target.value)}
                                        placeholder="Prénom"
                                        className={INPUT} />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                        Nom <span className="text-red-500">*</span>
                                    </label>
                                    <input type="text" value={lastName}
                                        onChange={e => setLastName(e.target.value)}
                                        placeholder="Nom"
                                        className={INPUT} />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                    Téléphone <span className="text-red-500">*</span>
                                </label>
                                <div className="flex gap-2">
                                    <select value={countryCode} onChange={e => setCountry(e.target.value)}
                                        className="w-24 px-2 py-3 border border-gray-300 rounded-lg text-sm bg-white
                      focus:outline-none focus:ring-2 focus:ring-teal-500">
                                        {COUNTRY_DIAL_CODES.map(c => (
                                            <option key={c.code} value={c.code}>{c.label}</option>
                                        ))}
                                    </select>
                                    <input type="tel" value={phone}
                                        onChange={e => setPhone(e.target.value.replace(/\D/g, ''))}
                                        placeholder="620 00 00 00"
                                        className={`flex-1 ${INPUT}`} />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                    E-mail <span className="text-gray-400">(optionnel)</span>
                                </label>
                                <input type="email" value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    placeholder="vous@exemple.com"
                                    className={INPUT} />
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                    Mot de passe <span className="text-red-500">*</span>
                                </label>
                                <input type="password" value={password}
                                    onChange={e => setPassword(e.target.value)}
                                    placeholder="8 caractères minimum"
                                    className={INPUT} />
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                    Confirmer le mot de passe <span className="text-red-500">*</span>
                                </label>
                                <input type="password" value={confirmPwd}
                                    onChange={e => setConfirmPwd(e.target.value)}
                                    placeholder="Répétez le mot de passe"
                                    className={`${INPUT} ${confirmPwd && confirmPwd !== password ? 'border-red-400' : ''}`} />
                                {confirmPwd && confirmPwd !== password && (
                                    <p className="text-red-500 text-xs mt-1">
                                        Les mots de passe ne correspondent pas.
                                    </p>
                                )}
                            </div>

                            <button type="submit" disabled={loading}
                                className="w-full py-3 bg-teal-600 text-white rounded-lg font-semibold
                  hover:bg-teal-700 transition-colors
                  disabled:opacity-50 disabled:cursor-not-allowed
                  flex items-center justify-center gap-2 mt-2">
                                {loading
                                    ? <><i className="ri-loader-4-line animate-spin" />Création…</>
                                    : <><i className="ri-user-add-line" />Rejoindre {invitation.entity_name}</>
                                }
                            </button>
                        </form>
                    </div>

                    <p className="text-center text-xs text-gray-400 mt-4">
                        Déjà un compte ?{' '}
                        <Link to="/login" className="text-teal-600 hover:underline">Se connecter</Link>
                    </p>
                </div>
            </div>
        </div>
    );
}